import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Save } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { AlertBanner, ContentCard, ui } from "../../../components/ui";
import { cn } from "../../../lib/utils";
import { createLead } from "../../../services/leadManagementService";
import {
  CREATE_FIELDS,
  canCreateLead,
  formFromLead,
  nextLeadForm,
  payloadFromForm,
  useLeadLookups,
  validateLeadForm,
} from "../leadForm";
import { LeadForm } from "../components/LeadForm";

const LEADS_PATH = "/crm/lead-management/leads";

/** Create Lead — a new lead is saved unassigned and opens on Lead Information. */
export function CreateLeadPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lookups = useLeadLookups();

  const [form, setForm] = useState(() => formFromLead());
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState("");

  const change = (key, value) => {
    setSaveError("");
    setErrors((current) => ({ ...current, [key]: "" }));
    setForm((current) => nextLeadForm(current, key, value));
  };

  const save = async (event) => {
    event.preventDefault();
    const found = validateLeadForm(form, { requireStatus: true });
    setErrors(found);
    if (Object.keys(found).length) {
      setSaveError(t("leadManagement.errorsFixFields"));
      return;
    }

    setBusy(true);
    setSaveError("");
    try {
      const created = await createLead(payloadFromForm(form, CREATE_FIELDS));
      navigate(`${LEADS_PATH}/${created.leadId}`, { replace: true, state: { flash: t("leadManagement.created") } });
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setBusy(false);
    }
  };

  // Only the lead-creating roles reach the form; anyone else is sent back to the list.
  if (!canCreateLead()) return <Navigate to={LEADS_PATH} replace />;

  return (
    <AppLayout className="space-y-5">
      <div className="mb-5">
        <PageTitleRow back={<PageBackLink to={LEADS_PATH} />}>
          <h1 className={ui.pageTitle}>{t("leadManagement.create")}</h1>
        </PageTitleRow>
      </div>

      <form onSubmit={save} className="space-y-5">
        {lookups.error ? <AlertBanner type="error">{lookups.error}</AlertBanner> : null}

        <ContentCard bodyClassName="form-section">
          <LeadForm
            mode="create"
            form={form}
            errors={errors}
            lookups={lookups}
            disabled={busy}
            onChange={change}
          />
        </ContentCard>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end form-actions">
          {saveError ? <p className="text-[12px] font-semibold text-rose-600 sm:mr-auto">{saveError}</p> : null}
          <button
            type="button"
            onClick={() => navigate(LEADS_PATH)}
            disabled={busy}
            className="h-11 rounded-xl border border-(--color-text-border) bg-white px-5 text-sm font-semibold text-foreground transition hover:bg-gray-50 disabled:opacity-50"
          >
            {t("formActions.cancel")}
          </button>
          <button type="submit" disabled={busy} className={cn(ui.primaryBtn, "px-6")}>
            <Save className="h-4 w-4" />
            {busy ? t("leadManagement.saving") : t("leadManagement.save")}
          </button>
        </div>
      </form>
    </AppLayout>
  );
}

export default CreateLeadPage;
